/**
 * Materiales personales del comercial (Sección Materiales): cartas para la
 * Comisión (formatos oficiales A/B/C), tarjeta con QR a su WhatsApp y firma de
 * mail. TODOS se completan solos con los datos del usuario comercial logueado
 * (nombre, celular, email — del doc `users`); no hay nada que tipear.
 *
 * Diseño de las cartas: réplica de los PDF "Habitia - Carta A/B/C" (sin la
 * promo del 50%, retirada 19-ago-2026). El teléfono y el email de contacto son
 * los del comercial: el lead es suyo.
 */

// FT Habit para preview/impresión (mismos assets que flyer/deck).
import ftRegular from "../assets/fonts/FTHabitUnlicensedTrial-Regular.otf";
import ftMedium from "../assets/fonts/FTHabitUnlicensedTrial-Medium.otf";

export const TIPO_PERSONAL = "material_personal";

export const PERSONALES = [
  { subtipo: "carta_a", nombre: "Carta para la Comisión — A (beneficios)", canal: "presencial", detalle: "Checklist de la app · lista para imprimir con tus datos" },
  { subtipo: "carta_b", nombre: "Carta para la Comisión — B (la app en 3 pasos)", canal: "presencial", detalle: "Hero + pasos del residente · lista para imprimir con tus datos" },
  { subtipo: "carta_c", nombre: "Carta para la Comisión — C (comparativa)", canal: "presencial", detalle: "Tradicional vs Habitia · lista para imprimir con tus datos" },
  { subtipo: "tarjeta", nombre: "Tarjeta personal con QR", canal: "presencial", detalle: "QR a TU WhatsApp · 4 pares frente/dorso por hoja" },
  { subtipo: "firma", nombre: "Firma de mail", canal: "email", detalle: "Con tus datos · copiar y pegar en Gmail" },
];

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const qr = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=1&color=111418&data=${encodeURIComponent(data)}`;

const telDigits = (tel) => String(tel || "").replace(/\D/g, "");

/** Normaliza el agente: nombre, cargo, email y tel con fallbacks seguros. */
function agenteNorm(agente = {}) {
  return {
    name: (agente.name || "").trim() || "Equipo Habitia",
    cargo: (agente.cargo || "Comercial").trim(),
    email: (agente.email || "info@habitia.ai").trim(),
    tel: (agente.tel || "").trim(),
  };
}

// ── Firma de mail ────────────────────────────────────────────────────────────

/** Solo la tabla de la firma (estilos inline, compatible Gmail/Outlook) — es lo que se copia. */
export function buildFirmaTabla(agente) {
  const a = agenteNorm(agente);
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="font-family:Helvetica,Arial,sans-serif;">
  <tr>
    <td style="padding-right:14px; border-right:2px solid #0080ff; vertical-align:top;">
      <div style="font-size:20px; color:#0080ff; letter-spacing:-0.5px; line-height:1;">habitia</div>
      <div style="font-size:10px; color:#6b7280; margin-top:3px;">administración<br>de edificios</div>
    </td>
    <td style="padding-left:14px; vertical-align:top;">
      <div style="font-size:14px; color:#1a1a1a;">${esc(a.name)}</div>
      <div style="font-size:12px; color:#6b7280; margin-top:2px;">${esc(a.cargo)} · Habitia</div>
      <div style="font-size:12px; color:#374151; margin-top:6px;">${esc(a.tel)}${a.tel ? " · " : ""}<a href="mailto:${esc(a.email)}" style="color:#0080ff; text-decoration:none;">${esc(a.email)}</a></div>
      <div style="font-size:12px; margin-top:2px;"><a href="https://habitia.ai" style="color:#0080ff; text-decoration:none;">habitia.ai</a> <span style="color:#9ca3af;">· La forma moderna de administrar edificios</span></div>
    </td>
  </tr>
</table>`;
}

// ── Piezas compartidas de las cartas ─────────────────────────────────────────

const FONTS_CSS = `
  @font-face { font-family:'FT Habit'; font-weight:400; src:url('${ftRegular}') format('opentype'); }
  @font-face { font-family:'FT Habit'; font-weight:500; src:url('${ftMedium}') format('opentype'); }`;

const CARTA_CSS = `
  * { box-sizing:border-box; }
  :root { --blue:#0080FF; --ink:#111418; --muted:#5a6672; --line:rgba(17,20,24,0.12); }
  body { margin:0; background:#e9eaec; font-family:'FT Habit','Helvetica Neue',Helvetica,Arial,sans-serif; color:var(--ink); -webkit-font-smoothing:antialiased; }
  .page { position:relative; width:210mm; min-height:297mm; margin:0 auto; background:#fff; padding:14mm 17mm 20mm; display:flex; flex-direction:column; }
  .page::after { content:""; position:absolute; left:0; right:0; bottom:0; height:2.6mm; background:var(--blue); }
  .head { display:flex; align-items:center; gap:10px; }
  .mark { display:flex; align-items:center; gap:4px; height:24px; }
  .mark span { width:6.5px; background:var(--blue); display:block; border-radius:1px; }
  .mark .t { height:24px; } .mark .m { height:9px; }
  .wm { font-size:19px; letter-spacing:-0.01em; }
  .head .right { margin-left:auto; text-align:right; font-size:11.5px; line-height:1.45; color:var(--muted); }
  .head .right a { color:var(--blue); text-decoration:none; }
  .rule { height:2px; background:var(--blue); margin:14px 0 0; }
  .salut { margin:26px 0 14px; font-size:15px; font-weight:500; }
  p.txt { margin:0 0 12px; font-size:14.5px; line-height:1.62; color:#2a323b; }
  .hb { color:var(--blue); font-weight:500; }
  b { font-weight:500; color:var(--ink); }
  .sign { margin-top:auto; padding-top:18px; }
  .sign p { margin:0; font-size:14.5px; line-height:1.5; }
  .sign .n { font-weight:500; margin-top:2px; }
  .sign .r { color:var(--muted); font-size:13.5px; }
  .foot { display:flex; justify-content:space-between; align-items:flex-end; border-top:1px solid var(--line); margin-top:16px; padding-top:12px; }
  .foot .l { font-size:12px; color:var(--muted); line-height:1.5; }
  .foot .l b { display:block; font-size:15px; color:var(--ink); }
  .foot .r { font-size:13px; color:var(--blue); }
  .qrpanel { display:flex; align-items:center; gap:22px; border:1px solid var(--line); border-radius:12px; padding:18px 22px; margin:16px 0 8px; }
  .qrpanel .tt { font-size:15.5px; font-weight:500; margin:0 0 5px; }
  .qrpanel .dd { font-size:12.5px; color:var(--muted); margin:0; line-height:1.5; max-width:34ch; }
  .qrs { margin-left:auto; display:flex; gap:20px; }
  .qr { text-align:center; }
  .qr img { width:84px; height:84px; display:block; margin:0 auto 6px; }
  .qr .k { font-size:9.5px; letter-spacing:0.1em; text-transform:uppercase; color:var(--blue); font-weight:500; }
  .qr .v { font-size:11.5px; color:var(--ink); }
  .checks { display:grid; grid-template-columns:1fr 1fr; column-gap:30px; row-gap:12px; margin:14px 0 4px; }
  .chk { display:flex; gap:10px; font-size:13.5px; line-height:1.5; color:#2a323b; }
  .chk i { flex-shrink:0; width:11px; height:11px; margin-top:4px; border:1.6px solid var(--blue); border-radius:999px; }
  .heroB { background:var(--blue); border-radius:14px; padding:24px 30px 26px; margin-top:16px; color:#fff; }
  .heroB .eb { margin:0 0 10px; font-size:11px; letter-spacing:0.14em; text-transform:uppercase; color:#cfe4ff; }
  .heroB h1 { margin:0; font-size:30px; line-height:1.14; font-weight:400; letter-spacing:-0.02em; max-width:19ch; }
  .stepsh { margin:20px 0 10px; font-size:12px; letter-spacing:0.1em; text-transform:uppercase; color:var(--muted); }
  .step { display:flex; gap:14px; margin-bottom:13px; font-size:14.5px; line-height:1.55; color:#2a323b; }
  .step i { flex-shrink:0; width:26px; height:26px; border:1.6px solid var(--blue); border-radius:999px; color:var(--blue); font-style:normal; display:flex; align-items:center; justify-content:center; font-size:13px; margin-top:1px; }
  .qrpanel.accent { border:none; background:#f2f7fd; border-left:4px solid var(--blue); border-radius:10px; }
  .cmp { width:100%; border-collapse:collapse; margin:16px 0 4px; border:1px solid var(--line); border-radius:10px; overflow:hidden; }
  .cmp th { font-size:11px; letter-spacing:0.1em; text-transform:uppercase; font-weight:500; text-align:left; padding:11px 16px; }
  .cmp th.trad { background:#f0f1f3; color:var(--muted); }
  .cmp th.hab { background:var(--blue); color:#fff; }
  .cmp td { font-size:13.5px; line-height:1.5; padding:12px 16px; border-top:1px solid var(--line); vertical-align:top; width:50%; }
  .cmp td.trad { color:var(--muted); }
  .cmp td .bb { color:var(--blue); font-weight:500; }
  .qrpanel.blue { background:var(--blue); border:none; color:#fff; }
  .qrpanel.blue .tt { color:#fff; }
  .qrpanel.blue .dd { color:rgba(255,255,255,0.88); }
  .qrpanel.blue .qr img { background:#fff; border-radius:8px; padding:5px; }
  .qrpanel.blue .qr .k { color:rgba(255,255,255,0.8); }
  .qrpanel.blue .qr .v { color:#fff; }
  @media print { body { background:#fff; } .page { margin:0; width:auto; } }`;

const cartaHead = (a, conRule) => `
  <div class="head">
    <div class="mark"><span class="t"></span><span class="m"></span><span class="t"></span></div>
    <span class="wm">habitia.ai</span>
    <div class="right">Montevideo, Uruguay<br><a href="mailto:${esc(a.email)}">${esc(a.email)}</a></div>
  </div>
  ${conRule ? '<div class="rule"></div>' : ""}`;

const cartaFirmaFoot = (a) => `
  <div class="sign">
    <p>Atentamente,</p>
    <p class="n">${esc(a.name)}</p>
    <p class="r">${esc(a.cargo)} · Habitia — Administración de edificios</p>
  </div>
  <div class="foot">
    <div class="l">Escribinos o llamanos<b>${esc(a.tel || "099 761 810")}</b></div>
    <div class="r">${esc(a.email)} · habitia.ai</div>
  </div>`;

const qrBloque = (clase, titulo, desc, qrs) => `
  <div class="qrpanel ${clase}">
    <div><p class="tt">${titulo}</p><p class="dd">${desc}</p></div>
    <div class="qrs">${qrs.map((x) => `
      <div class="qr"><img src="${x.src}" alt="QR ${x.k}"><div class="k">${x.k}</div><div class="v">${x.v}</div></div>`).join("")}
    </div>
  </div>`;

const QR_WEB = { src: qr("https://habitia.ai"), k: "Sitio web", v: "habitia.ai" };
const QR_DEMO = { src: qr("https://app.habitia.ai/solicitar-demo"), k: "Demo", v: "Acceso directo" };
const QR_IG = { src: qr("https://www.instagram.com/habitia"), k: "Instagram", v: "@habitia" };

const cartaDoc = (titulo, body) => `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"><title>${titulo}</title>
<style>${FONTS_CSS}${CARTA_CSS}</style></head>
<body><div class="page">${body}</div></body></html>`;

function cartaA(a) {
  return cartaDoc("Habitia — Carta A", `
  ${cartaHead(a, true)}
  <p class="salut">Estimados integrantes de la Comisión del edificio:</p>
  <p class="txt">Nos presentamos: somos <span class="hb">Habitia</span>, una administración de edificios que combina la gestión de siempre con tecnología propia para hacerla más simple, ordenada y cercana.</p>
  <p class="txt">A diferencia de una administración tradicional, en Habitia cada propietario y residente tiene la información de su edificio en una app: estado de cuenta al día, las facturas y gastos comunes, novedades, documentos y reservas de amenities, todo en un mismo lugar y disponible las 24 horas.</p>
  <p class="txt">Como administración integral, nos ocupamos de que el edificio funcione y de que cada propietario lo entienda con claridad:</p>
  <div class="checks">
    <div class="chk"><i></i><span><b>Liquidaciones claras</b>, con el detalle de cada gasto del mes.</span></div>
    <div class="chk"><i></i><span><b>Facturas y comprobantes</b> del edificio disponibles para consultar.</span></div>
    <div class="chk"><i></i><span><b>Pago de gastos comunes</b> desde la plataforma.</span></div>
    <div class="chk"><i></i><span><b>Reserva de amenities</b> en pocos toques.</span></div>
    <div class="chk"><i></i><span><b>Novedades y comunicados oficiales</b> del edificio, al instante.</span></div>
    <div class="chk"><i></i><span><b>Actas, reglamento y proveedores</b> siempre a mano.</span></div>
    <div class="chk"><i></i><span><b>Habi</b>, asistente con IA que responde 24/7 con toda la información del edificio.</span></div>
  </div>
  <p class="txt" style="margin-top:14px;">Además, cotizamos el presupuesto de su edificio <b>sin costo y sin compromiso</b>.</p>
  <p class="txt">Nos encantaría conversar con la Comisión y mostrarles en detalle cómo trabajamos. Quedamos a disposición para coordinar una reunión sin compromiso.</p>
  ${qrBloque("", "Conocé Habitia en un minuto", "Escaneá el código con la cámara del teléfono y accedé a toda la información de nuestro servicio.", [QR_WEB, QR_IG])}
  ${cartaFirmaFoot(a)}`);
}

function cartaB(a) {
  return cartaDoc("Habitia — Carta B", `
  ${cartaHead(a, false)}
  <div class="heroB">
    <p class="eb">Administración de edificios</p>
    <h1>La administración de tu edificio, ahora en tu teléfono.</h1>
  </div>
  <p class="salut">Estimados integrantes de la Comisión del edificio:</p>
  <p class="txt">Somos <span class="hb">Habitia</span>. Combinamos una administración profesional con una app propia, para que la vida del edificio sea simple, ordenada y clara para todos los que viven en él.</p>
  <p class="stepsh">Así de simple lo vive cada residente</p>
  <div class="step"><i>1</i><span><b>Abre la app y ve todo.</b> Estado de cuenta al día, gastos del edificio, novedades y documentos, disponibles las 24 horas.</span></div>
  <div class="step"><i>2</i><span><b>Resuelve sin llamadas.</b> Paga los gastos comunes, reserva amenities y reporta una incidencia con una foto, siguiendo su estado hasta la solución.</span></div>
  <div class="step"><i>3</i><span><b>Pregunta cuando quiera.</b> Habi, el asistente con IA, responde al instante sobre cuentas, asambleas o reglamento, a cualquier hora.</span></div>
  <p class="txt" style="margin-top:6px;">Además, cotizamos el presupuesto de su edificio <b>sin costo y sin compromiso</b>.</p>
  <p class="txt">Nos encantaría mostrarles todo esto en vivo. Quedamos a disposición para coordinar una reunión con la Comisión, sin compromiso.</p>
  ${qrBloque("accent", "Probá la app ahora mismo", "Escaneá el código con la cámara del teléfono y accedé directo a una demo interactiva de Habitia.", [QR_DEMO, QR_IG])}
  ${cartaFirmaFoot(a)}`);
}

function cartaC(a) {
  return cartaDoc("Habitia — Carta C", `
  ${cartaHead(a, false)}
  <p class="salut" style="margin-top:30px;">Estimados integrantes de la Comisión del edificio:</p>
  <p class="txt">Elegir la administración del edificio es una decisión importante. Por eso queremos mostrarles, en concreto, qué cambia en el día a día de la Comisión y de cada residente con <span class="hb">Habitia</span>.</p>
  <table class="cmp">
    <tr><th class="trad">Administración tradicional</th><th class="hab">Con Habitia</th></tr>
    <tr><td class="trad">Consultas de saldo y gastos por teléfono o mail.</td><td><span class="bb">Cuentas y facturas</span> del edificio, claras y a la vista en la app.</td></tr>
    <tr><td class="trad">Reclamos que se pierden en llamados.</td><td><span class="bb">Incidencias con foto</span> y seguimiento hasta que se resuelven.</td></tr>
    <tr><td class="trad">Reservas y avisos mandando un email a la administración o llamando.</td><td><span class="bb">Amenities, novedades y documentos</span> en un solo lugar.</td></tr>
    <tr><td class="trad">Respuestas solo en horario de oficina.</td><td><span class="bb">Habi</span>, asistente con IA que responde 24/7.</td></tr>
  </table>
  <p class="txt" style="margin-top:14px;">Además, cotizamos el presupuesto de su edificio <b>sin costo y sin compromiso</b>.</p>
  <p class="txt">Nos encantaría conversar con la Comisión y mostrarles en detalle cómo trabajamos. Quedamos a disposición para coordinar una reunión sin compromiso.</p>
  ${qrBloque("blue", "Conocenos y probá la app", "Escaneá con la cámara del teléfono: web, demo interactiva o nuestro Instagram.", [QR_WEB, QR_DEMO, QR_IG])}
  ${cartaFirmaFoot(a)}`);
}

// ── Tarjeta personal ─────────────────────────────────────────────────────────

function tarjeta(a) {
  const dig = telDigits(a.tel);
  const wa = dig ? `https://wa.me/${dig}?text=${encodeURIComponent("Hola, tengo la tarjeta de Habitia. Quiero información para mi edificio.")}` : "https://habitia.ai";
  const front = `
    <div class="card front">
      <div>
        <div class="logo"><span class="bars"><i></i><i class="short"></i><i></i></span><span>habitia.ai</span></div>
        <div class="tagline">La forma moderna de administrar edificios</div>
      </div>
      <div class="who">
        <div class="name">${esc(a.name)}</div>
        <div class="role">${esc(a.cargo)} · Habitia</div>
        <div class="tel">${esc(a.tel)}</div>
      </div>
    </div>`;
  const back = `
    <div class="card back">
      <img src="${qr(wa)}" alt="QR a WhatsApp de ${esc(a.name)}">
      <div class="cta">Escaneá y <b>escribime por WhatsApp</b>: te cotizo la administración de tu edificio en el día.<div class="web">habitia.ai</div></div>
    </div>`;
  return `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"><title>Habitia — Tarjeta ${esc(a.name)}</title>
<style>${FONTS_CSS}
  * { box-sizing:border-box; }
  body { margin:0; padding:16px; background:#e9eaec; font-family:'FT Habit','Helvetica Neue',Helvetica,Arial,sans-serif; }
  .sheet { display:flex; flex-wrap:wrap; gap:10mm; justify-content:center; }
  .card { width:90mm; height:55mm; border-radius:3mm; overflow:hidden; background:#fff; page-break-inside:avoid; outline:0.2mm solid #d1d5db; }
  .front { background:#0080FF; color:#fff; padding:7mm 8mm; display:flex; flex-direction:column; justify-content:space-between; }
  .logo { display:flex; align-items:flex-end; gap:2mm; }
  .bars { display:flex; align-items:center; gap:1.2mm; height:7mm; }
  .bars i { display:block; width:1.5mm; background:#fff; height:7mm; }
  .bars i.short { height:2.5mm; }
  .logo span:last-child { font-size:15px; letter-spacing:-0.01em; padding-left:1.5mm; }
  .tagline { font-size:9.5px; color:rgba(255,255,255,0.85); margin-top:1.5mm; }
  .who .name { font-size:17px; font-weight:500; letter-spacing:-0.01em; }
  .who .role { font-size:10.5px; color:rgba(255,255,255,0.9); margin-top:0.8mm; }
  .who .tel { font-size:11px; margin-top:1.6mm; }
  .back { padding:6mm 7mm; display:flex; align-items:center; gap:6mm; }
  .back img { width:33mm; height:33mm; display:block; }
  .back .cta { font-size:12px; color:#1a1a1a; line-height:1.45; }
  .back .cta b { font-weight:500; color:#0080FF; }
  .back .web { font-size:10.5px; color:#6b7280; margin-top:2mm; }
  @media print { body { background:#fff; padding:0; } .card { outline:0.2mm solid #d1d5db; } }
</style></head>
<body><div class="sheet">${(front + back).repeat(4)}</div></body></html>`;
}

// ── Firma (preview) ──────────────────────────────────────────────────────────

function firmaPreview(a) {
  return `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"><title>Habitia — Firma de mail</title>
<style>body { margin:0; padding:36px; background:#f5f7fa; font-family:Helvetica,Arial,sans-serif; }
.box { max-width:640px; margin:0 auto; background:#fff; border-radius:10px; padding:26px; box-shadow:0 1px 3px rgba(0,0,0,0.08); }</style>
</head><body><div class="box">${buildFirmaTabla(a)}</div></body></html>`;
}

/** HTML completo del material personal según subtipo, con los datos del agente logueado. */
export function buildPersonalHtml(subtipo, agente) {
  const a = agenteNorm(agente);
  switch (subtipo) {
    case "carta_a": return cartaA(a);
    case "carta_b": return cartaB(a);
    case "carta_c": return cartaC(a);
    case "tarjeta": return tarjeta(a);
    case "firma": return firmaPreview(a);
    default: return cartaA(a);
  }
}
